import { useMemo, useState, useEffect } from 'react'
import {
  GetWalletListDTO,
  WalletItemModel,
  WalletPaginationModel,
} from '@/services/apis/wallet/models'
import walletApi from '@/services/apis/wallet'
import emitter from '@/utils/event'
import { REFRESH_WALLET_DETAIL_EVENT } from '@/constants/event'
import BigNumber from 'bignumber.js'
import { formatDayChangeAmount, formatDayChangeRate, toFixed } from '@/utils/number'
import { WalletAssetsModel } from '@/services/apis/wallet/models'
import { useSelector, useDispatch } from 'react-redux'
import { AppState, AppDispatch } from '@/store/types'
import { getWalletList, setWalletsQuery, WalletSliceState } from '@/store/wallet'
import txRecordApi from '@/services/apis/txRecord'
import { Web3SignPaginationModel } from '@/services/apis/txRecord/models'
import { DEFAULT_PAGE_SIZE } from '@/constants'
import { formatUSD } from '@/utils/format'
import { LocalBackupStateEnum } from '@/constants/enum'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'

export const useGetWeb3SignRecords = (walletKey: string) => {
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [data, setData] = useState<Web3SignPaginationModel>()
  useEffect(() => {
    setLoading(true)
    txRecordApi
      .getWeb3SignRecords({
        pageNumber: currentPage,
        queryParam: {
          accountKey: walletKey,
        },
      })
      .then(res => {
        if (res.success) {
          setData(res.data)
        }
      })
      .finally(() => setLoading(false))
  }, [currentPage])
  useEffect(() => {
    setCurrentPage(1)
  }, [walletKey])
  return {
    data,
    loading,
    currentPage,
    setCurrentPage,
  }
}

export const useGetWalletDetail = (walletKey?: string) => {
  const [isLoading, setIsLoading] = useState(false)
  const [walletDetail, setWalletDetail] = useState<WalletItemModel>()
  const fn = () => {
    if (walletKey) {
      setIsLoading(true)
      walletApi
        .getVaultAccountDetail({ accountKey: walletKey })
        .then(res => {
          const { data } = res
          if (data) {
            setWalletDetail(data)
          }
        })
        .finally(() => setIsLoading(false))
    }
  }
  useEffect(fn, [walletKey])
  useEffect(() => {
    emitter.on(REFRESH_WALLET_DETAIL_EVENT, fn)
    return () => emitter.off(REFRESH_WALLET_DETAIL_EVENT)
  }, [])
  return {
    data: walletDetail,
    isLoading,
  }
}

export const useFormatedAsset = (walletAssets: WalletAssetsModel | null) => {
  const { openUsdBalance, balanceUsd } = walletAssets ?? {}

  const changedAmountPercent = useMemo(() => {
    return formatDayChangeRate(openUsdBalance, balanceUsd)
  }, [openUsdBalance, balanceUsd])

  const changedAmount = useMemo(() => {
    return formatDayChangeAmount(openUsdBalance, balanceUsd)
  }, [walletAssets?.balanceUsd, walletAssets?.openUsdBalance])

  const appAssets = useMemo(() => {
    return (
      walletAssets?.visibleTypeBalanceList?.find?.(v => v.accountVisibleType === 0)?.balanceUsd ??
      '0'
    )
  }, [walletAssets])

  const appAssetsPercent = useMemo(() => {
    if (!walletAssets?.balanceUsd || new BigNumber(walletAssets.balanceUsd).isEqualTo(0)) {
      return '0'
    }
    return new BigNumber(appAssets)
      .dividedBy(new BigNumber(walletAssets.balanceUsd))
      .multipliedBy(100)
      .toFixed()
  }, [appAssets, walletAssets])

  const apiAssets = useMemo(() => {
    return (
      walletAssets?.visibleTypeBalanceList?.find(v => v.accountVisibleType === 1)?.balanceUsd ?? '0'
    )
  }, [walletAssets])

  const apiAssetsPercent = useMemo(() => {
    if (!walletAssets?.balanceUsd || new BigNumber(walletAssets.balanceUsd).isEqualTo(0)) {
      return '0'
    }
    return new BigNumber(apiAssets)
      .dividedBy(new BigNumber(walletAssets.balanceUsd))
      .multipliedBy(100)
      .toFixed()
  }, [apiAssets, walletAssets])

  const totalAssets = useMemo(() => {
    const balance = walletAssets?.balanceUsd
    if (!balance || new BigNumber(balance).isEqualTo(0)) {
      return '0'
    }
    return balance
  }, [walletAssets?.balanceUsd])

  const assets = {
    totalAssets,
    apiAssets,
    apiAssetsPercent,
    appAssets,
    appAssetsPercent,
  }
  const percentVal = ['apiAssetsPercent', 'appAssetsPercent']

  type AssetKey = keyof typeof assets

  const fixedAssets = (Object.keys(assets) as AssetKey[]).reduce((accum, key) => {
    const val = assets[key]
    const isPercent = percentVal.indexOf(key) > -1
    if (isPercent) {
      accum[key] = toFixed(val, 2)
    } else {
      accum[key] = formatUSD(val)
    }
    return accum
  }, assets)
  return {
    ...fixedAssets,
    changedAmount,
    changedAmountPercent,
  }
}

export const useGetWalletAssets = (workspaceKey: string) => {
  const [walletAssets, setWalletAssets] = useState<WalletAssetsModel | null>(null)

  useEffect(() => {
    walletApi.getTotalBalance().then(res => {
      setWalletAssets(res.data)
    })
  }, [workspaceKey])

  return useFormatedAsset(walletAssets)
}

const defaultWalletPagination = {
  totalElements: 0,
  totalPages: 0,
  content: [],
  pageNumber: 0,
}

export const useGetAssetWallets = (searchVal?: string, filteredWalletKey?: string) => {
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<WalletPaginationModel>(defaultWalletPagination)
  useEffect(() => {
    setIsLoading(true)
    walletApi
      .getWalletList({
        accountType: 1,
        name: searchVal,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: currentPage,
      })
      .then(res => {
        if (res.success) {
          const content = res.data.content.filter(wallet => wallet.accountKey !== filteredWalletKey)
          setPagination({
            ...res.data,
            content,
          })
          return
        }
        setPagination(defaultWalletPagination)
      })
      .finally(() => setIsLoading(false))
  }, [currentPage, searchVal])
  return {
    isLoading,
    data: pagination,
    currentPage,
    setCurrentPage,
  }
}

export const useGetWalletList = (dep: string) => {
  const { isGetWalletsLoading, walletsPagination, walletsQuery, walletsRefresh } = useSelector<
    AppState,
    WalletSliceState
  >(state => state.wallet)
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    dispatch(
      getWalletList({
        ...walletsQuery,
      })
    )
  }, [walletsQuery, walletsRefresh, dep])
  return {
    isLoading: isGetWalletsLoading,
    data: walletsPagination,
    query: walletsQuery,
    setQuery: (data: GetWalletListDTO) => dispatch(setWalletsQuery(data)),
  }
}

export const useSelectWalletsSearchValue = () => {
  return useSelector<AppState, string>(state => state.wallet.walletsSearchValue)
}
export const useGetIsSendAddressRecoveryFile = (backupState?: LocalBackupStateEnum) => {
  const [isSendAddressRecoveryFile, setIsSendAddressRecoveryFile] = useState(true)
  const location = useLocation()

  useEffect(() => {
    if (backupState === LocalBackupStateEnum.COMPLETE) {
      walletApi.isSendAddressRecoveryFile().then(res => {
        setIsSendAddressRecoveryFile(res.data)
      })
    }
  }, [queryString.parse(location.search)?.wkey, backupState])
  return {
    isSendAddressRecoveryFile,
    setIsSendAddressRecoveryFile,
  }
}
